/**
 * Bloq filename utility — mirrors backend `core/filenames.py`.
 *
 * Build:  [Meeting Title]-[MMDDYYYY]-[HHmm-24h]-[type].[ext]
 * Example: "Meeting with Bloq Founder-06102026-1330-recall-report.pdf"
 *
 * Use this anywhere we trigger a client-side download so file names stay
 * consistent across the platform.
 */

const BAD_CHARS = /[\\/:*?"<>|\x00-\x1f]/g;
const WS = /\s+/g;

export function sanitizeTitle(title, maxLen = 80) {
  if (!title) return "Meeting";
  const cleaned = String(title)
    .replace(BAD_CHARS, "_")
    .replace(WS, " ")
    .trim()
    .replace(/^\.+|\.+$/g, "");
  if (!cleaned) return "Meeting";
  return cleaned.slice(0, maxLen);
}

/**
 * Format `dateLike` (Date | ISO string) in the requested IANA timezone using
 * 24-hour clock for filename purposes.
 *
 * Returns { date: "MMDDYYYY", time: "HHmm" }.
 */
function dateParts(dateLike, tz) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) {
    return { date: "00000000", time: "0000" };
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz || "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return {
    date: `${get("month")}${get("day")}${get("year")}`,
    time: `${get("hour").replace("24", "00")}${get("minute")}`,
  };
}

export function buildMeetingFilename({ title, whenIso, downloadType, extension, timezone }) {
  const { date, time } = dateParts(whenIso, timezone);
  const dtype = String(downloadType || "file").trim().toLowerCase().replace(WS, "-");
  const ext = String(extension || "txt").replace(/^\./, "").toLowerCase();
  return `${sanitizeTitle(title)}-${date}-${time}-${dtype}.${ext}`;
}

/**
 * Trigger a browser download from a Blob using a built filename.
 * If `responseBlob` is a Response from fetch with a Content-Disposition,
 * caller should prefer that name; otherwise we generate one here.
 */
export function saveBlobAs(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}
